package com.siriusxm.pia.views.unifiedaggregator.events

import androidx.compose.runtime.Composable
import com.siriusxm.cmc.utils.getString
import com.siriusxm.pia.SXMUI
import com.siriusxm.pia.components.simpleSelect
import unifiedcontent.aggregator.CloudEvent
import unifiedcontent.aggregator.ContentUpdateType
import kotlinx.serialization.json.jsonObject
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

/**
 * Types of entity events we allow users to filter.
 */
enum class EventTypeFilterOption(val label: String) {
    ADD("Add"),
    UPDATE("Update"),
    REMOVE("Remove")
}

/**
 * Attempts to map a CloudEvent to one of the supported filter buckets.
 */
fun CloudEvent.eventTypeFilterOption(): EventTypeFilterOption? {
    val tracingUpdateType = tracingcontext?.getString("updateType")
        ?.let { ContentUpdateType.fromName(it) }
        ?.toFilterOption()
    if (tracingUpdateType != null) {
        return tracingUpdateType
    }

    val payloadType = data?.jsonObject?.getString("type")
        ?.let { ContentUpdateType.fromName(it) }
        ?.toFilterOption()
    if (payloadType != null) {
        return payloadType
    }

    val typeSuffix = type.substringAfterLast('.')
    val suffixMatch = ContentUpdateType.fromName(typeSuffix)
        ?.toFilterOption()
        ?: fallbackFilterMatch(typeSuffix)
    return suffixMatch
}

private fun ContentUpdateType?.toFilterOption(): EventTypeFilterOption? {
    return when (this) {
        ContentUpdateType.ADD -> EventTypeFilterOption.ADD
        ContentUpdateType.UPDATE -> EventTypeFilterOption.UPDATE
        ContentUpdateType.REMOVE -> EventTypeFilterOption.REMOVE
        else -> null
    }
}

private fun fallbackFilterMatch(rawType: String): EventTypeFilterOption? {
    val normalized = rawType.lowercase()
    return when {
        normalized.contains("add") -> EventTypeFilterOption.ADD
        normalized.contains("remove") || normalized.contains("delete") -> EventTypeFilterOption.REMOVE
        normalized.contains("update") -> EventTypeFilterOption.UPDATE
        else -> null
    }
}

/**
 * Returns a filtered copy of the events list when selections are active.
 */
fun List<CloudEvent>.filterByEventTypes(filters: Set<EventTypeFilterOption>): List<CloudEvent> {
    if (filters.isEmpty()) {
        return this
    }
    return this.filter { event ->
        val option = event.eventTypeFilterOption()
        option != null && option in filters
    }
}

fun EventTypeFilterOption?.asFilterSet(): Set<EventTypeFilterOption> =
    this?.let { setOf(it) } ?: emptySet()

/**
 * Render the event type dropdown & clear affordance.
 */
private const val ALL_EVENT_SELECTION = "__ALL__"

@Composable
fun EventTypeFilterControl(
    selectedType: EventTypeFilterOption?,
    onSelectionChange: (EventTypeFilterOption?) -> Unit
) {
    Div({
        classes(EventTypeFilterStyles.container)
    }) {
        Div({ classes(EventTypeFilterStyles.trigger) }) {
            val currentSelection = selectedType?.name ?: ALL_EVENT_SELECTION
            val options = listOf("All event types" to ALL_EVENT_SELECTION) +
                EventTypeFilterOption.entries.map { option -> option.label to option.name }

            simpleSelect(currentSelection, options) { selected ->
                if (selected == ALL_EVENT_SELECTION) {
                    onSelectionChange(null)
                } else {
                    val selection = EventTypeFilterOption.valueOf(selected)
                    onSelectionChange(selection)
                }
            }
        }
    }
}

object EventTypeFilterStyles : StyleSheet() {
    val container by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(8.px)
    }

    val trigger by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(6.px)
        border(1.px, LineStyle.Solid, SXMUI.borderDividerDefault.value())
        borderRadius(SXMUI.boxRadius.value())
        padding(0.35.em, 0.6.em)

        type("select") style {
            border(0.px)
            fontSize(0.95.em)
            fontWeight(500)
            color(SXMUI.textColor.value())
        }
    }
}
